import type { ClientWsMessage, ServerWsMessage } from '@rw/shared';

type Listener = (msg: ServerWsMessage) => void;

/** Reconnecting WebSocket client for game events. */
export class RealtimeClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<(connected: boolean) => void>();
  private retry = 1_000;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private token: string | null = null;
  private closedByUs = false;
  connected = false;

  constructor() {
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => this.sendVisibility());
  }

  connect(token: string) {
    if (this.token === token && this.ws) return;
    this.disconnect();
    this.token = token;
    this.closedByUs = false;
    this.open();
  }

  disconnect() {
    this.closedByUs = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.ws?.close();
    this.ws = null;
    this.token = null;
    this.setConnected(false);
  }

  send(msg: ClientWsMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  onStatus(fn: (connected: boolean) => void) {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  /** Reconnect immediately (e.g. when the network comes back). */
  nudge() {
    if (!this.token || this.ws) return;
    if (this.timer) clearTimeout(this.timer);
    this.retry = 1_000;
    this.open();
  }

  private open() {
    if (!this.token) return;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws?token=${encodeURIComponent(this.token)}`);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 1_000;
      this.setConnected(true);
      this.sendVisibility();
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(String(ev.data)) as ServerWsMessage;
        for (const l of this.listeners) l(msg);
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onclose = (ev) => {
      this.ws = null;
      this.setConnected(false);
      if (this.closedByUs || ev.code === 4401) return;
      this.timer = setTimeout(() => this.open(), this.retry);
      this.retry = Math.min(this.retry * 2, 30_000);
    };
    ws.onerror = () => ws.close();
  }

  /** The server skips push notifications while the app is on screen. */
  sendVisibility() {
    this.send({ type: 'visibility', visible: document.visibilityState === 'visible' });
  }

  private setConnected(c: boolean) {
    if (this.connected === c) return;
    this.connected = c;
    for (const l of this.statusListeners) l(c);
  }
}
